import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Booking,
  BookingGroup,
  BookingGroupService,
  BookingService,
  Job,
  JobService,
} from 'us-web-services';
import {
  formatDateRange,
  getFullDateString,
  getMonthDayString,
} from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const CAREGIVER_TYPE_AGENCY = 2;

// Mirrors QuotesService.jobQuotes in apps/angular/src/js/ng/common/services/quotes-service.js.
// Only rendered when the group has no jobs (the upsell-to-post-a-job path).
const JOB_QUOTE = {
  title: 'More Caregivers = Better Odds',
  text:
    "I always suggest posting a job along with sending a request. Whether you're a new family making your first hire or an UrbanSitter veteran adding to your roster, the Job Board is an easy and fast way to find new caregivers.",
  name: 'Del, Member Services',
  picUrl: 'https://assets.urbansitter.com/static/img/photos/del.webp',
};

const EXPANDED_CODES = ['pending', 'accepted', 'closed_completed', 'parent_reschedule'];
const CLOSED_STATUS_KEYS = new Set([
  'declined',
  'expired',
  'closed_duplicate',
  'parent_cancel',
  'sitter_cancel',
]);

interface GroupWithChildren extends BookingGroup {
  bookings?: Booking[];
  jobs?: Job[];
}

interface BookingWithStatus extends Booking {
  status?: Booking['status'] & { code?: string; label?: string; webIcon?: string };
  caregiver?: Booking['caregiver'] & { active?: boolean };
  caregiverType?: number;
  parent?: Booking['parent'];
}

export const BookingSummary = observer((): JSX.Element => {
  const { t } = useTranslation();
  const { id: pathId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [group, setGroup] = useState<GroupWithChildren | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Booking Summary';
  }, []);

  useEffect(() => {
    const groupId = Number(pathId);
    if (!groupId || !user?.id) return;
    let cancelled = false;

    const load = async (): Promise<void> => {
      let g: GroupWithChildren;
      try {
        const resp = await BookingGroupService.get(groupId, { params: { parentId: user.id } });
        if (cancelled) return;
        g = resp.data?.data as GroupWithChildren;
        if (!g) {
          navigate('/bookings');
          return;
        }
        const next: GroupWithChildren = { ...g, jobs: [], bookings: [] };
        setGroup(next);
      } catch {
        navigate('/bookings');
        return;
      }

      void (async (): Promise<void> => {
        try {
          const jr = await JobService.getByFilter({
            params: { groupId: g.id, userId: user.id, parentId: user.id },
          });
          if (cancelled) return;
          setGroup((prev) => (prev ? { ...prev, jobs: jr.data?.data ?? [] } : prev));
        } catch {
          /* ignore */
        }
      })();

      try {
        const br = await BookingService.getByFilter({
          params: { groupId: g.id, userId: user.id, parentId: user.id },
        });
        if (cancelled) return;
        setGroup((prev) => (prev ? { ...prev, bookings: br.data?.data ?? [] } : prev));
      } catch {
        navigate('/bookings');
      }
    };
    void load();

    return (): void => {
      cancelled = true;
    };
  }, [pathId, user?.id]);

  const bookings = (group?.bookings ?? []) as BookingWithStatus[];
  const firstBooking = bookings.length === 1;
  const jobs = group?.jobs ?? [];

  const startDate = useMemo(
    () => (group?.start ? new Date(group.start) : null),
    [group?.start],
  );

  const headline = useMemo((): { title: string; subtitle?: string; hidePrompt: boolean } => {
    if (!group || !startDate) {
      return { title: '', hidePrompt: false };
    }
    if (startDate < new Date()) {
      // group in the past
      return {
        title:
          group.status === 'fulfilled_us'
            ? t('pages.bookingSummary.titleCompleted')
            : t('pages.bookingSummary.titleClosed'),
        hidePrompt: true,
      };
    }

    let filled = 0;
    let pending = 0;
    let pendingBooking: BookingWithStatus | undefined;
    for (const b of bookings) {
      const code = b.status?.code ?? '';
      if (!CLOSED_STATUS_KEYS.has(code) && code !== 'pending' && code !== 'parent_reschedule') {
        filled += 1;
      }
      if (code === 'pending') {
        pending += 1;
        pendingBooking = b;
      }
    }
    const stillNeed = (group.numberOfSitters ?? 0) - filled;

    if (stillNeed > 0) {
      if (pending < stillNeed) {
        return { title: t('pages.bookingSummary.titleReachOut'), hidePrompt: false };
      }
      const title = t('pages.bookingSummary.titlePending');
      if (pending === 1 && pendingBooking?.caregiver?.firstName) {
        return {
          title,
          subtitle: t('pages.bookingSummary.subWillContact', {
            name: pendingBooking.caregiver.firstName,
          }),
          hidePrompt: false,
        };
      }
      const key =
        stillNeed === 1
          ? 'pages.bookingSummary.subSingleNeeded'
          : 'pages.bookingSummary.subMultipleNeeded';
      return { title, subtitle: t(key, { count: stillNeed }), hidePrompt: false };
    }

    return {
      title: t('pages.bookingSummary.titleConfirmed'),
      subtitle: t('pages.bookingSummary.subConfirmed'),
      hidePrompt: true,
    };
  }, [group, startDate, bookings, t]);

  const requestSummary = ((): string => {
    if (!group?.numberOfSitters || !startDate) return '';
    const key =
      group.numberOfSitters === 1
        ? 'pages.bookingSummary.requestSummary'
        : 'pages.bookingSummary.requestSummaryPlural';
    return t(key, {
      count: group.numberOfSitters,
      date: getMonthDayString(startDate),
    });
  })();

  const createJob = async (): Promise<void> => {
    if (!group?.id || bookings.length === 0 || creating) return;
    setCreating(true);
    const booking = bookings[0];
    try {
      await JobService.create({
        groupId: group.id,
        type: 'one_time',
        status: 'new',
        reason: 'one_click_post',
        booking: {
          parent: booking.parent,
          address: booking.address,
          type: booking.type,
          typeId: booking.typeId,
          paymentType: booking.paymentType,
          start: booking.start,
          end: booking.end,
          rate: booking.rate,
          kidsAges: booking.kidsAges,
          numberOfChildren: booking.numberOfChildren,
          sweeteners: booking.sweeteners,
          includes: booking.includes,
          description: `Looking for a sitter for ${getFullDateString(startDate ?? undefined)}`,
        },
      } as never);
      navigate('/posts?jobPosted=3');
    } catch (err: unknown) {
      setCreating(false);
      const msgs: string[] = [];
      const anyErr = err as { response?: { data?: { errors?: Array<{ message?: string }> } } };
      for (const e of anyErr?.response?.data?.errors ?? []) {
        if (e?.message) msgs.push(e.message);
      }
      toast.error(msgs.length ? msgs.join(' · ') : 'Unable to post job. Try again.');
    }
  };

  const previewJob = (): void => {
    if (!group?.bookings?.[0]?.id) return;
    navigate(`/job-post?bookingId=${group.bookings[0].id}`);
  };

  const firstSitterFirstName = bookings[0]?.caregiver?.firstName;

  if (!group) return <div className='container py-8' />;

  return (
    <div className='container py-8'>
      <h1 className='mb-0 text-2xl font-semibold'>{headline.title}</h1>
      {headline.subtitle && <div className='mb-5 text-neutral-700'>{headline.subtitle}</div>}

      <div className='grid grid-cols-1 md:grid-cols-12 gap-6 mt-5'>
        {/* Left column: request summary + booking rows + post-job prompt */}
        <div className='md:col-span-9'>
          <h3 className='mb-2 text-lg font-semibold'>{requestSummary}</h3>
          <div className='us-card p-4 rounded'>
            {bookings.length === 0 && (
              <div>{t('pages.bookingSummary.noRequests')}</div>
            )}

            {bookings.map((booking) => {
              const code = booking.status?.code ?? '';
              const isExpanded = EXPANDED_CODES.includes(code);
              const closedSuffix = CLOSED_STATUS_KEYS.has(code)
                ? t(`pages.bookingSummary.closedStatus.${code}`)
                : '';

              if (isExpanded) {
                return (
                  <div key={booking.id} className='py-3'>
                    <div className='flex items-start gap-4'>
                      <div className='flex-shrink-0 relative w-20 h-20'>
                        <span className={`booking-status-icon absolute top-0 right-0 icon-sm icon-svg-${booking.status?.webIcon ?? ''}`} />
                        {booking.caregiver?.active !== false ? (
                          <a
                            id={`expanded-to-booking-${booking.id}`}
                            href={`/app/bookings/${booking.id}`}
                          >
                            <img
                              alt={booking.caregiver?.firstName ?? ''}
                              src={booking.caregiver?.pictureUrl}
                              className='w-20 h-20 rounded object-cover'
                            />
                          </a>
                        ) : (
                          <img
                            alt={booking.caregiver?.firstName ?? ''}
                            src={booking.caregiver?.pictureUrl}
                            className='w-20 h-20 rounded object-cover'
                          />
                        )}
                      </div>
                      <div className='flex-1'>
                        <h4 className='mb-0 capitalize font-semibold'>{booking.status?.label}</h4>
                        <div>{formatDateRange(booking.start, booking.end)}</div>
                        <a
                          id={`booking-details-${booking.id}`}
                          href={`/app/bookings/${booking.id}`}
                          className='underline'
                        >
                          {t('pages.bookingSummary.viewDetails')}
                        </a>
                        {booking.caregiverType !== CAREGIVER_TYPE_AGENCY && booking.parent?.id && (
                          <>
                            {' - '}
                            <a
                              id={`message-thread-${booking.caregiver?.id}`}
                              href={`/app/parent/${booking.parent.id}/messages/${booking.caregiver?.id}`}
                              className='underline'
                            >
                              {t('pages.bookingSummary.messageName', {
                                name: booking.caregiver?.firstName ?? '',
                              })}
                            </a>
                          </>
                        )}
                        <h4 className='mb-0 font-semibold'>
                          {booking.caregiver?.firstName}
                          {booking.caregiver?.active === false && t('pages.bookingSummary.noLongerActive')}
                        </h4>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={booking.id} id={`collapsed-booking-${booking.id}`} className='py-3'>
                  <h5 className='mb-0 font-semibold'>
                    {booking.caregiver?.firstName}
                    {closedSuffix}
                  </h5>
                  <a
                    id={`collapsed-to-booking-${booking.id}`}
                    href={`/app/bookings/${booking.id}`}
                    className='underline'
                  >
                    {t('pages.bookingSummary.viewDetailsCapital')}
                  </a>
                </div>
              );
            })}

            {(jobs.length > 0 || !headline.hidePrompt) && <hr className='mt-3' />}

            {jobs.map((job) => {
              const closed = (job.status ?? '').indexOf('closed') === 0;
              const applicants = (job as { numberOfApplicants?: number }).numberOfApplicants ?? 0;
              return (
                <div id='interested-sitters' key={job.id} className='mb-6'>
                  <h4 className='mb-1 font-semibold'>
                    {closed
                      ? t('pages.bookingSummary.jobStatusClosed')
                      : t('pages.bookingSummary.jobStatusOpen')}
                  </h4>
                  <p>{t('pages.bookingSummary.interestedSitters', { count: applicants })}</p>
                  {applicants > 0 ? (
                    <a
                      id={`job-interest-cta-${job.id}`}
                      className='btn btn-primary inline-block'
                      href={`/app/job/${job.id}/interest`}
                    >
                      {t('pages.bookingSummary.viewInterested')}
                    </a>
                  ) : (
                    <a
                      id={`job-details-cta-${job.id}`}
                      className='btn btn-secondary inline-block'
                      href={`/app/job/${job.id}`}
                    >
                      {t('pages.bookingSummary.viewJobPost')}
                    </a>
                  )}
                </div>
              );
            })}

            {jobs.length === 0 && !headline.hidePrompt && (
              <div className='mb-6'>
                <h4 className='mb-1 font-semibold'>
                  {firstBooking
                    ? t('pages.bookingSummary.postBackup')
                    : t('pages.bookingSummary.postFind')}
                </h4>
                <ul className='list-none mb-5'>
                  <li>
                    {firstBooking && firstSitterFirstName
                      ? t('pages.bookingSummary.bullet1WithName', { name: firstSitterFirstName })
                      : t('pages.bookingSummary.bullet1Generic')}
                  </li>
                  <li>{t('pages.bookingSummary.bullet2')}</li>
                  <li>
                    {t('pages.bookingSummary.bullet3WillClose')}
                    {firstBooking && firstSitterFirstName
                      ? t('pages.bookingSummary.bullet3IfAccepts', { name: firstSitterFirstName })
                      : t('pages.bookingSummary.bullet3WhenFilled')}
                  </li>
                </ul>
                <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
                  <div className='md:col-span-5'>
                    <Button
                      id={`one-click-post-${group.id}`}
                      buttonType={ButtonType.primary}
                      className='w-full'
                      onClick={createJob}
                      disabled={creating}
                    >
                      {t('pages.bookingSummary.postToCommunity')}
                    </Button>
                  </div>
                  <div className='md:col-span-5'>
                    <Button
                      id={`preview-post-${group.id}`}
                      buttonType={ButtonType.secondary}
                      className='w-full'
                      onClick={previewJob}
                    >
                      {t('pages.bookingSummary.previewFirst')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right column: Del quote, only when the upsell-to-post panel shows */}
        {jobs.length === 0 && !headline.hidePrompt && (
          <aside className='md:col-span-3 text-center' id='job-side-quote'>
            <h4 className='font-semibold'>{JOB_QUOTE.title}</h4>
            <div className='italic my-2'>&ldquo;{JOB_QUOTE.text}&rdquo;</div>
            <h6 className='text-right flex items-center justify-end gap-2 text-sm'>
              &ndash; {JOB_QUOTE.name}
              <img
                alt={JOB_QUOTE.name}
                src={JOB_QUOTE.picUrl}
                className='w-12 h-12 rounded-full object-cover'
              />
            </h6>
          </aside>
        )}
      </div>
    </div>
  );
});
